import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { ButtonSplitGroup } from '@components/Buttons';
import { CheckoutBackordersInfo } from '@components/Checkout/CheckoutBackordersInfo';
import { CheckoutEmptyCart } from '@components/Checkout/CheckoutEmptyCart';
import { CheckoutServiceRestrictions } from '@components/Checkout/CheckoutServiceRestrictions';
import { CheckoutTable } from '@components/Checkout/CheckoutTable';
import { CheckoutTermsAndConditions } from '@components/Checkout/CheckoutTermsAndConditions';
import { EmptyBasketModal } from '@components/Checkout/Modals';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { Page500 } from '@containers/ErrorPages';
import { cartApi } from '@reducers/order/order';

const baseSchema = Yup.object().shape({
  termsAndConditions: Yup.boolean()
    .oneOf([true], 'Please accept Terms and Conditions to process order.')
    .required(),
});

export const Checkout = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, isFetching } =
    cartApi.useGetCheckoutSummaryQuery();
  const [processOrder, result] = cartApi.useProcessOrderCartMutation();
  const [validationSchema, setValidationSchema] = useState(baseSchema);

  useEffect(() => {
    if (!data) {
      return;
    }
    if (data.restrictions.length > 0) {
      setValidationSchema(
        baseSchema.shape({
          serviceRestrictions: Yup.boolean()
            .oneOf([true], 'You must accept service restrictions to proceed.')
            .required(),
        })
      );
    }
  }, [data]);
  if (isLoading || isFetching) {
    return <DefaultSpinnerCenter />;
  }
  if (isError) {
    return <Page500 />;
  }
  if (!data || data.results.length === 0) {
    return <CheckoutEmptyCart />;
  }

  return (
    <>
      <Card>
        <Card.Header className="bg-didxl text-white">
          Shopping Cart Checkout
        </Card.Header>
        <Card.Body>
          <CheckoutTable data={data.results} />
        </Card.Body>
      </Card>
      <Card className="mt-3">
        <Card.Header className="bg-didxl text-white" />
        <Card.Body>
          <Formik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={{
              serviceRestrictions: false,
              termsAndConditions: false,
            }}
            onSubmit={async (_, actions) => {
              actions.setSubmitting(true);
              processOrder()
                .unwrap()
                .then(() => {
                  navigate(`/order/incomplete`, {
                    state: { fromCheckout: true },
                  });
                })
                .catch(() => {
                  actions.setFieldError('name', 'Server-side error occurred.');
                  actions.setSubmitting(false);
                });
            }}
          >
            {({ handleSubmit, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <CheckoutServiceRestrictions data={data.restrictions} />
                <CheckoutBackordersInfo />
                <CheckoutTermsAndConditions />
                <Row>
                  <Col>
                    <ButtonSplitGroup>
                      <EmptyBasketModal />
                      <Button
                        size="sm"
                        variant="didxl"
                        onClick={() => navigate('/order/numbers')}
                      >
                        Continue shopping
                      </Button>
                      <Button
                        size="sm"
                        type="submit"
                        variant="didxl"
                        disabled={result.isLoading || isSubmitting}
                      >
                        Process order
                      </Button>
                    </ButtonSplitGroup>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </>
  );
};
